import { useState, useEffect, useRef } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { RefreshCw, Power, Link, Shuffle, Trash2, StopCircle } from 'lucide-react'
import { toast } from 'sonner'

interface AuthTask {
  id: string
  action: 'init' | 'next_host' | 'change_proxy'
  status: 'pending' | 'running' | 'stopping' | 'stopped' | 'success' | 'failed'
  started_at: number | null
  finished_at: number | null
  error: string | null
}

interface AuthStatus {
  remarks_name: string
  plat: string
  host: string
  hosts: string[]
  error_hosts: string[]
  proxy: {
    http: string
    https: string
  } | null
  timeout_count: number
  error_count: number
  last_change_proxy_time: string | null
  is_in_init: boolean
  state: 'active' | 'destroying' | 'destroyed'
  instance_ready?: boolean
  current_task?: AuthTask | null
  last_task?: AuthTask | null
}

interface LogEntry {
  seq: number
  session: string
  level: 'INFO' | 'WARNING' | 'ERROR' | 'DEBUG'
  title: string
  message: string
  meta: Record<string, any>
  timestamp: number
}

interface AuthSessionPanelProps {
  accountInfo: {
    remarks_name: string
    plat: string
  }
}

const mergeLogs = (current: LogEntry[], incoming: LogEntry[]) => {
  const logsBySeq = new Map(current.map(log => [log.seq, log]))
  incoming.forEach(log => logsBySeq.set(log.seq, log))
  return [...logsBySeq.values()].sort((a, b) => a.seq - b.seq)
}

// 获取 base URL（使用 VITE_PROXY_URL）
const getBaseUrl = () => {
  return import.meta.env.VITE_PROXY_URL || ''
}

export function AuthSessionPanel({ accountInfo }: AuthSessionPanelProps) {
  const [status, setStatus] = useState<AuthStatus | null>(null)
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [executingAction, setExecutingAction] = useState(false)
  const [stopping, setStopping] = useState(false)
  const [destroying, setDestroying] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const eventSourceRef = useRef<{ close: () => void } | null>(null)
  const logsViewportRef = useRef<HTMLDivElement>(null)
  const shouldFollowLogsRef = useRef(true)
  const lastSeqRef = useRef(0)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const connectionIdRef = useRef(0)

  const remarks = accountInfo.remarks_name
  const encodedRemarks = encodeURIComponent(remarks)
  const isTaskRunning = Boolean(
    status?.current_task &&
    ['pending', 'running', 'stopping'].includes(status.current_task.status)
  )

  // 获取 token
  const getToken = () => localStorage.getItem('token')

  // 获取当前状态（使用 fetch 因为是 GET 请求）
  const fetchStatus = async () => {
    try {
      const token = getToken()
      const baseUrl = getBaseUrl()
      const resp = await fetch(`${baseUrl}/mobile/auth/${encodedRemarks}/status`, {
        method: 'GET',
        headers: {
          'Authorization': token || '',
        },
      })
      const data = await resp.json()
      if (data.code === 0) {
        setStatus(data.data)
        if (!data.data?.current_task) {
          setStopping(false)
        }
      } else if (resp.status === 404) {
        setStatus(null)
        setStopping(false)
      }
    } catch (error) {
      console.error('Failed to fetch status:', error)
    }
  }

  // 获取历史日志（使用 fetch 因为是 GET 请求）
  const fetchLogs = async (since: number = 0): Promise<number> => {
    try {
      const token = getToken()
      const baseUrl = getBaseUrl()
      const resp = await fetch(`${baseUrl}/mobile/auth/${encodedRemarks}/logs?since=${since}`, {
        method: 'GET',
        headers: {
          'Authorization': token || '',
        },
      })
      const data = await resp.json()
      if (data.code === 0) {
        const incomingLogs: LogEntry[] = data.data.logs || []
        setLogs(prev => mergeLogs(prev, incomingLogs))
        lastSeqRef.current = Math.max(lastSeqRef.current, data.data.last_seq || 0)
        return lastSeqRef.current
      }
    } catch (error) {
      console.error('Failed to fetch logs:', error)
    }
    return lastSeqRef.current
  }

  const closeLogStream = () => {
    connectionIdRef.current += 1
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
    eventSourceRef.current?.close()
    eventSourceRef.current = null
    setIsConnected(false)
  }

  const startLogStream = async () => {
    closeLogStream()
    const connectionId = connectionIdRef.current
    const lastSeq = await fetchLogs(lastSeqRef.current)
    void connectSSE(connectionId, lastSeq)
  }

  // 使用 fetch + ReadableStream 实现 SSE（支持自定义请求头）
  const connectSSE = async (connectionId: number, since: number = lastSeqRef.current) => {
    if (connectionId !== connectionIdRef.current) return

    // 清除之前的重连定时器
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }

    // 关闭现有连接
    if (eventSourceRef.current) {
      try {
        eventSourceRef.current.close()
      } catch (e) {
        // ignore
      }
    }

    const token = getToken()
    if (!token) {
      console.error('No token found')
      return
    }

    const baseUrl = getBaseUrl()
    const url = `${baseUrl}/mobile/auth/${encodedRemarks}/logs/stream?since=${since}`

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Accept': 'text/event-stream',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      if (connectionId !== connectionIdRef.current) {
        await response.body?.cancel()
        return
      }

      setIsConnected(true)
      console.log('SSE connected')

      const reader = response.body?.getReader()
      const decoder = new TextDecoder()

      if (!reader) {
        throw new Error('No reader available')
      }

      // 保存 reader 用于断开连接
      eventSourceRef.current = {
        close: () => {
          reader.cancel()
        }
      }

      let buffer = ''
      let eventType = 'message'
      let eventDataLines: string[] = []

      // 读取流
      const readStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read()

            if (done) {
              if (connectionId !== connectionIdRef.current) return
              setIsConnected(false)
              void fetchStatus()
              reconnectTimerRef.current = setTimeout(
                () => connectSSE(connectionId, lastSeqRef.current),
                5000
              )
              break
            }
            if (connectionId !== connectionIdRef.current) {
              await reader.cancel()
              return
            }

            // 解码数据
            buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n')

            // 按行分割
            const lines = buffer.split('\n')
            buffer = lines.pop() || '' // 保留最后不完整的行

            for (const line of lines) {
              if (line.startsWith('event:')) {
                eventType = line.slice(6).trim()
              } else if (line.startsWith('data:')) {
                eventDataLines.push(line.slice(5).trimStart())
              } else if (line === '') {
                // 空行表示一个事件结束
                if (eventDataLines.length > 0) {
                  handleSSEEvent(eventType, eventDataLines.join('\n'))
                  eventType = 'message'
                  eventDataLines = []
                }
              } else if (line.startsWith(':')) {
                // 注释行（keepalive ping），忽略
                continue
              }
            }
          }
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            console.log('SSE connection aborted')
          } else {
            if (connectionId !== connectionIdRef.current) return
            console.error('SSE read error:', error)
            setIsConnected(false)
            reconnectTimerRef.current = setTimeout(
              () => connectSSE(connectionId, lastSeqRef.current),
              5000
            )
          }
        }
      }

      readStream()
    } catch (error) {
      if (connectionId !== connectionIdRef.current) return
      console.error('SSE connection error:', error)
      setIsConnected(false)
      reconnectTimerRef.current = setTimeout(
        () => connectSSE(connectionId, lastSeqRef.current),
        5000
      )
    }
  }

  // 处理 SSE 事件
  const handleSSEEvent = (eventType: string, data: string) => {
    try {
      if (eventType === 'status') {
        const statusData = JSON.parse(data)
        setStatus(statusData)
        if (!statusData.current_task) {
          setStopping(false)
          closeLogStream()
          void fetchStatus()
        }
      } else if (eventType === 'log') {
        const logData: LogEntry = JSON.parse(data)
        setLogs(prev => mergeLogs(prev, [logData]))
        lastSeqRef.current = Math.max(lastSeqRef.current, logData.seq)
      } else if (eventType === 'destroyed') {
        connectionIdRef.current += 1
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current)
          reconnectTimerRef.current = null
        }
        setStatus(null)
        setStopping(false)
        setIsConnected(false)
      }
    } catch (error) {
      console.error('Failed to parse SSE event:', error)
    }
  }

  // 初始化
  useEffect(() => {
    closeLogStream()
    setStatus(null)
    setLogs([])
    setStopping(false)
    setDestroying(false)
    setExecutingAction(false)
    setIsConnected(false)
    lastSeqRef.current = 0
    shouldFollowLogsRef.current = true

    const initialize = async () => {
      await fetchStatus()
    }

    initialize()

    return () => {
      closeLogStream()
    }
  }, [remarks])

  // 仅在 Auth 方法执行期间接收日志；状态由 SSE 事件驱动，不持续轮询。
  useEffect(() => {
    if (!isTaskRunning) {
      closeLogStream()
      return
    }

    if (!eventSourceRef.current) {
      void startLogStream()
    }

    const timer = window.setInterval(() => {
      fetchLogs(lastSeqRef.current)
    }, 1000)

    return () => window.clearInterval(timer)
  }, [remarks, isTaskRunning])

  // 自动滚动到日志底部
  useEffect(() => {
    const viewport = logsViewportRef.current
    if (!viewport || !shouldFollowLogsRef.current) return

    const frame = requestAnimationFrame(() => {
      viewport.scrollTop = viewport.scrollHeight
    })

    return () => cancelAnimationFrame(frame)
  }, [logs])

  const handleLogsScroll = () => {
    const viewport = logsViewportRef.current
    if (!viewport) return

    const distanceToBottom =
      viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight
    shouldFollowLogsRef.current = distanceToBottom < 48
  }

  // 执行操作（使用 fetch）
  const executeAction = async (action: 'init' | 'next_host' | 'change_proxy', body?: any) => {
    if (executingAction || destroying) return

    // 检查是否有任务正在执行
    if (isTaskRunning) {
      toast.error('已有任务执行中，请等待完成或使用"停止"/"销毁"')
      return
    }

    setExecutingAction(true)
    try {
      const token = getToken()
      const baseUrl = getBaseUrl()
      const path = action === 'init'
        ? `/mobile/auth/${encodedRemarks}/init`
        : `/mobile/auth/${encodedRemarks}/${action}`

      const resp = await fetch(`${baseUrl}${path}`, {
        method: 'POST',
        headers: {
          'Authorization': token || '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })

      const data = await resp.json()

      if (data.code === 0) {
        toast.success(data.msg || '操作成功')
        setLogs([])
        lastSeqRef.current = 0
        if (data.data?.status) {
          setStatus(data.data.status)
        }
      } else if (data.code === 409) {
        toast.warning(data.msg || '已有任务执行中，请等待完成或使用"停止"/"销毁"')
        if (data.data?.status) {
          setStatus(data.data.status)
        }
      } else {
        toast.error(data.msg || '操作失败')
      }
    } catch (error) {
      console.error('Failed to execute action:', error)
      toast.error('操作失败')
    } finally {
      setExecutingAction(false)
    }
  }

  // 初始化
  const handleInit = () => {
    executeAction('init', {
      plat: accountInfo.plat,
      is_proxy: true,
      duration: null,
      strict: false,
      recreate: false,
    })
  }

  // 切换 host
  const handleNextHost = () => {
    executeAction('next_host')
  }

  // 切换代理
  const handleChangeProxy = () => {
    executeAction('change_proxy')
  }

  // 停止任务（使用 fetch）
  const handleStop = async () => {
    if (stopping || destroying) return

    setStopping(true)
    try {
      const token = getToken()
      const baseUrl = getBaseUrl()
      const resp = await fetch(`${baseUrl}/mobile/auth/${encodedRemarks}/stop`, {
        method: 'POST',
        headers: {
          'Authorization': token || '',
          'Content-Type': 'application/json',
        },
      })

      const data = await resp.json()

      if (data.code === 0) {
        toast.success(data.msg || '已请求停止任务')
        if (data.data?.status) {
          setStatus(data.data.status)
        }
      } else {
        setStopping(false)
        toast.error(data.msg || '停止失败')
      }
    } catch (error) {
      setStopping(false)
      console.error('Failed to stop task:', error)
      toast.error('停止失败')
    }
  }

  // 销毁（使用 fetch 因为是 DELETE 请求）
  const handleDestroy = async () => {
    if (destroying) return

    if (!confirm('确定要销毁会话吗？销毁后需要重新初始化。')) {
      return
    }

    setDestroying(true)
    try {
      const token = getToken()
      const baseUrl = getBaseUrl()
      const resp = await fetch(`${baseUrl}/mobile/auth/${encodedRemarks}`, {
        method: 'DELETE',
        headers: {
          'Authorization': token || '',
        },
      })

      const data = await resp.json()

      if (data.code === 0) {
        const nextStatus: AuthStatus | null = data.data?.status || null
        if (nextStatus && nextStatus.state === 'destroying') {
          setStatus(nextStatus)
          setStopping(true)
          toast.success(data.msg || '已请求销毁，正在停止任务')
        } else {
          toast.success(data.msg || '会话已销毁')
          closeLogStream()
          setStopping(false)
          lastSeqRef.current = 0
          setStatus(null)
          setLogs([])
        }
      } else {
        if (data.data?.status) {
          setStatus(data.data.status)
        }
        toast.error(data.msg || '销毁失败')
      }
    } catch (error) {
      console.error('Failed to destroy:', error)
      toast.error('销毁失败')
    } finally {
      setDestroying(false)
    }
  }

  // 刷新状态
  const handleRefresh = () => {
    fetchStatus()
  }

  // 判断操作按钮是否应该禁用（init/next_host/change_proxy）
  const isOperationButtonDisabled = () => {
    if (executingAction || stopping || destroying) return true
    if (!status) return false
    if (status.state === 'destroyed') return false
    if (status.state === 'destroying') return true
    return isTaskRunning
  }

  const isStopButtonDisabled = () => {
    if (
      stopping ||
      destroying ||
      !status ||
      status.state === 'destroyed' ||
      status.state === 'destroying'
    ) return true
    return !isTaskRunning
  }

  const isDestroyButtonDisabled = () => {
    return destroying || !status || status.state !== 'active'
  }

  // 获取状态颜色
  const getStatusColor = (state: string) => {
    switch (state) {
      case 'active':
        return 'bg-green-500'
      case 'destroyed':
        return 'bg-gray-500'
      case 'destroying':
        return 'bg-yellow-500'
      default:
        return 'bg-yellow-500'
    }
  }

  // 获取任务状态颜色
  const getTaskStatusColor = (taskStatus: string) => {
    switch (taskStatus) {
      case 'pending':
        return 'default'
      case 'running':
        return 'default'
      case 'success':
        return 'default'
      case 'failed':
        return 'destructive'
      case 'stopped':
      case 'stopping':
        return 'secondary'
      default:
        return 'secondary'
    }
  }

  // 获取日志级别颜色
  const getLogLevelColor = (level: string) => {
    switch (level) {
      case 'ERROR':
        return 'text-red-500'
      case 'WARNING':
        return 'text-yellow-500'
      case 'INFO':
        return 'text-blue-500'
      case 'DEBUG':
        return 'text-gray-500'
      default:
        return 'text-foreground'
    }
  }

  // 格式化时间
  const formatTime = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">认证会话管理</CardTitle>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <div
                className={`w-2 h-2 rounded-full ${
                  isConnected
                    ? 'bg-green-500'
                    : isTaskRunning
                      ? 'bg-yellow-500'
                      : 'bg-gray-400'
                }`}
              />
              <span className="text-xs text-muted-foreground">
                {isConnected ? '日志已连接' : isTaskRunning ? '日志连接中' : '空闲'}
              </span>
            </div>
            <Button variant="ghost" size="sm" onClick={handleRefresh}>
              <RefreshCw className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* 状态显示 */}
        {status && (
          <div className="space-y-3">
            {/* 基本状态 */}
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="bg-muted/30 rounded-lg p-2">
                <p className="text-xs text-muted-foreground mb-1">状态</p>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${getStatusColor(status.state)}`} />
                  <span className="font-medium">{status.state}</span>
                </div>
              </div>
              <div className="bg-muted/30 rounded-lg p-2">
                <p className="text-xs text-muted-foreground mb-1">初始化中</p>
                <p className="font-medium">{status.is_in_init ? '是' : '否'}</p>
              </div>
              <div className="bg-muted/30 rounded-lg p-2 col-span-2">
                <p className="text-xs text-muted-foreground mb-1">Host</p>
                <p className="font-medium text-xs break-all">{status.host || '-'}</p>
              </div>
              {status.proxy && (
                <div className="bg-muted/30 rounded-lg p-2 col-span-2">
                  <p className="text-xs text-muted-foreground mb-1">代理</p>
                  <p className="font-medium text-xs">{status.proxy.http || '-'}</p>
                </div>
              )}
              <div className="bg-muted/30 rounded-lg p-2">
                <p className="text-xs text-muted-foreground mb-1">超时次数</p>
                <p className="font-medium">{status.timeout_count}</p>
              </div>
              <div className="bg-muted/30 rounded-lg p-2">
                <p className="text-xs text-muted-foreground mb-1">错误次数</p>
                <p className="font-medium">{status.error_count}</p>
              </div>
            </div>

            {/* 当前任务或最近一次任务 */}
            {(status.current_task || status.last_task) && (() => {
              const task = status.current_task || status.last_task!
              return (
              <div className="bg-muted/30 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold">
                    {status.current_task ? '当前任务' : '最近任务'}
                  </p>
                  <Badge variant={getTaskStatusColor(task.status)}>
                    {task.status}
                  </Badge>
                </div>
                <div className="space-y-1 text-xs">
                  <p><span className="text-muted-foreground">动作:</span> {task.action}</p>
                  <p><span className="text-muted-foreground">任务ID:</span> {task.id.slice(0, 8)}...</p>
                  {task.started_at && (
                    <p><span className="text-muted-foreground">开始时间:</span> {formatTime(task.started_at)}</p>
                  )}
                  {task.finished_at && (
                    <p><span className="text-muted-foreground">结束时间:</span> {formatTime(task.finished_at)}</p>
                  )}
                  {task.error && (
                    <p className="text-red-500"><span className="text-muted-foreground">错误:</span> {task.error}</p>
                  )}
                </div>
              </div>
              )
            })()}
          </div>
        )}

        {/* 操作按钮 */}
        <div className="grid grid-cols-2 gap-2">
          <Button
            size="sm"
            onClick={handleInit}
            disabled={isOperationButtonDisabled()}
          >
            <Power className="h-3 w-3 mr-1" />
            {status ? '重新初始化' : '初始化'}
          </Button>
          <Button
            size="sm"
            onClick={handleNextHost}
            disabled={
              isOperationButtonDisabled() ||
              !status ||
              status.state !== 'active' ||
              status.instance_ready === false
            }
          >
            <Link className="h-3 w-3 mr-1" />
            切换Host
          </Button>
          <Button
            size="sm"
            onClick={handleChangeProxy}
            disabled={
              isOperationButtonDisabled() ||
              !status ||
              status.state !== 'active' ||
              status.instance_ready === false
            }
          >
            <Shuffle className="h-3 w-3 mr-1" />
            切换代理
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleStop}
            disabled={isStopButtonDisabled()}
          >
            <StopCircle className="h-3 w-3 mr-1" />
            {stopping ? '停止中' : '停止'}
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={handleDestroy}
            disabled={isDestroyButtonDisabled()}
            className="col-span-2"
          >
            <Trash2 className="h-3 w-3 mr-1" />
            {destroying || status?.state === 'destroying' ? '销毁中' : '销毁'}
          </Button>
        </div>

        {/* 日志面板 */}
        <div>
          <h3 className="text-sm font-semibold mb-2">实时日志</h3>
          <div
            ref={logsViewportRef}
            className="h-64 overflow-y-auto overscroll-contain touch-pan-y border rounded-lg bg-muted/20"
            onScroll={handleLogsScroll}
          >
            <div className="p-2 space-y-1">
              {logs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">暂无日志</p>
              ) : (
                logs.map((log) => {
                  // 处理 message，可能是对象或字符串
                  const messageContent = typeof log.message === 'object'
                    ? JSON.stringify(log.message, null, 2)
                    : log.message

                  return (
                    <div key={log.seq} className="text-xs font-mono border-b border-muted/20 pb-1 mb-1 last:border-0">
                      <div className="flex items-start gap-2">
                        <span className="text-muted-foreground whitespace-nowrap">[{formatTime(log.timestamp)}]</span>
                        <span className={`${getLogLevelColor(log.level)} whitespace-nowrap`}>[{log.level}]</span>
                        <span className="font-semibold whitespace-nowrap">{log.title}:</span>
                      </div>
                      <div className="mt-1 pl-2 whitespace-pre-wrap break-words text-foreground/90">
                        {messageContent}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
